import React from "react";
/** Бандл исполняется внутри сборочного пререндера (headless Chrome). */
export const IS_PRERENDER = typeof window !== "undefined" && Boolean(window.__PRERENDER__);
/**
 * Что лежит в снимке главной `/`. ЕДИНСТВЕННЫЙ переключатель:
 *   true  — лендинг WelcomePage (свои title/description/H1);
 *   false — как у живого гостя вне AB-ветки: редирект на /generate-song
 *           (тогда `/` надо убрать из sitemap.xml — снимок станет генератором,
 *           а проверка снимков потребует у него свой H1 и canonical).
 * Читает `isLandingActive` (shared/lib/landingActive.ts).
 */
export const PRERENDER_HOME_AS_LANDING = true;
/**
 * Показывать ли ЛЮДЯМ статику снимка главной `/` до загрузки JS.
 *
 * true (сейчас) — снимок лендинга видят все: `/` стала статичным лендингом для
 * каждого неавторизованного посетителя, показ лендинга больше не AB. Робот и
 * клиент без JS получают тот же HTML, что и человек, — первый экран приходит
 * без ожидания бандла.
 *
 * false — статика `/` скрыта у всех, у кого исполняется JS: посетитель видит
 * обычный старт приложения (лоадер → приложение решает, что рисовать). HTML
 * снимка при этом по-прежнему отдаётся всем — роботам и клиентам без JS.
 *
 * Это ЕДИНСТВЕННОЕ место переключения: webpack.config.js читает значение отсюда
 * и подставляет в инлайн-гейт public/index.html.
 *
 * Посетителя с сессией аккаунта снимок главной всё равно не касается: его
 * отсекает общее правило «страница шелла у залогиненного» (в статике боковое
 * меню гостевое), и там же приложение уводит его в студию.
 */
export const HOME_STATIC_VISIBLE = true;
/** Страховка: если страница так и не сообщила о готовности — показываем как есть. */
const HANDOFF_TIMEOUT_MS = 10000;
/** Сколько ждём декодирования картинок живого дерева перед подменой. */
const HANDOFF_IMAGES_TIMEOUT_MS = 1500;
let pendingCommit = null;
let liveRoot = null;
let timeoutId = null;
let completing = false;
/** Живое дерево ещё рендерится «за кулисами», на экране — статика снимка. */
export const isPrerenderHandoffPending = () => pendingCommit !== null;
/**
 * Первый рендер страницы должен совпасть со статикой: без анимаций появления.
 * Истинно и в самом пререндере (снимок снимается без анимаций), и пока идёт
 * подмена (иначе контент, который человек уже видит, «въехал» бы второй раз).
 */
export const isStaticFirstRender = () => IS_PRERENDER || isPrerenderHandoffPending();
/**
 * Начать подмену. `commit` — атомарная замена статики живым контейнером
 * (её знает index.tsx). Вызовется ровно один раз: по `usePageReady`, по уходу
 * с пререндеренного адреса или по таймауту.
 */
export function beginPrerenderHandoff(root, commit) {
    liveRoot = root;
    pendingCommit = commit;
    timeoutId = setTimeout(() => runCommit(), HANDOFF_TIMEOUT_MS);
}
function runCommit() {
    const commit = pendingCommit;
    pendingCommit = null;
    liveRoot = null;
    if (timeoutId)
        clearTimeout(timeoutId);
    timeoutId = null;
    commit?.();
}
/**
 * Картинки живого дерева создаются заново (другие DOM-узлы), и хотя байты уже в
 * кэше после статики, декодирование асинхронное: без ожидания первый кадр после
 * подмены показал бы пустые <img> (а `ImageWithLoader` — свои плейсхолдеры).
 * Ждём недолго и только неленивые: `loading="lazy"` вне документа не грузится.
 */
function waitForImages(root) {
    const images = Array.from(root.querySelectorAll("img")).filter((img) => img.loading !== "lazy" && Boolean(img.currentSrc || img.src));
    if (!images.length)
        return Promise.resolve();
    const decoded = Promise.all(images.map((img) => typeof img.decode === "function"
        ? img.decode().catch(() => undefined)
        : Promise.resolve()));
    return new Promise((resolve) => {
        const timer = setTimeout(resolve, HANDOFF_IMAGES_TIMEOUT_MS);
        void decoded.then(() => {
            clearTimeout(timer);
            // Один тик: дать отработать onLoad-обработчикам и перерисоваться React.
            setTimeout(resolve, 0);
        });
    });
}
/** Страница готова — заменить статику живым деревом (идемпотентно). */
export function completePrerenderHandoff() {
    if (!pendingCommit || completing)
        return;
    completing = true;
    const root = liveRoot;
    void (root ? waitForImages(root) : Promise.resolve()).then(() => {
        completing = false;
        runCommit();
    });
}
/**
 * Сообщить, что страница отрисовала своё содержимое (а не лоадер).
 *   - в пререндере: сигнал скрипту «можно снимать DOM»; `error` валит сборку
 *     понятным сообщением (например, API чартов недоступен);
 *   - у посетителя: момент подмены статики живым деревом.
 * Вне этих двух режимов — no-op.
 */
export function usePageReady(ready = true, error) {
    React.useEffect(() => {
        if (IS_PRERENDER) {
            if (error)
                window.__PRERENDER_ERROR__ = error;
            else if (ready)
                window.__PRERENDER_READY__ = true;
            return;
        }
        if (ready)
            completePrerenderHandoff();
    }, [ready, error]);
}
